/**
 * Sentinel values used in statement conditions at catalogue/definition time.
 * At ability-build time they are replaced by resolved id arrays.
 */
export const RESTAURANT_SCOPE_PLACEHOLDER = '$RESTAURANT_SCOPE' as const;
export const ORG_SCOPE_PLACEHOLDER = '$ORG_SCOPE' as const;
/**
 * Used by restaurant-scoped statements whose CASL condition is keyed on the organization
 * (e.g. user creation → { organizationIds: ur.restaurant.organizationId }). At build time it
 * resolves to the organization ids of the restaurants in the assignment's resolved scope.
 */
export const ORG_FROM_RESTAURANT_SCOPE_PLACEHOLDER = '$ORG_FROM_RESTAURANT_SCOPE' as const;

export type ScopePlaceholder =
    | typeof RESTAURANT_SCOPE_PLACEHOLDER
    | typeof ORG_SCOPE_PLACEHOLDER
    | typeof ORG_FROM_RESTAURANT_SCOPE_PLACEHOLDER;

export type PolicyScope = 'organization' | 'restaurant';

/** Detects whether a (one-level-deep) conditions object references a given scope placeholder. */
export function conditionsReferencePlaceholder(conditions: Record<string, unknown>, placeholder: ScopePlaceholder): boolean {
    return Object.values(conditions).some((value) => {
        if (value === placeholder) return true;
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            return Object.values(value as Record<string, unknown>).some((inner) => inner === placeholder);
        }
        return false;
    });
}

/**
 * Determines a statement's scope from the placeholder it binds.
 * Scope is read from the placeholder rather than the subject, because a subject (e.g. Restaurant, User) can legitimately appear at both scopes with different conditions.
 * `$ORG_SCOPE` resolves to the organization bucket.
 * `$RESTAURANT_SCOPE` / `$ORG_FROM_RESTAURANT_SCOPE` resolve to the restaurant bucket.
 * A statement that binds no scope placeholder, or that mixes organization and restaurant placeholders, returns null so the builder skips it and validation rejects it.
 */
export function getStatementScope(conditions: Record<string, unknown> | undefined): PolicyScope | null {
    if (!conditions) return null;
    const bindsOrgScope = conditionsReferencePlaceholder(conditions, ORG_SCOPE_PLACEHOLDER);
    const bindsRestaurantScope =
        conditionsReferencePlaceholder(conditions, RESTAURANT_SCOPE_PLACEHOLDER) ||
        conditionsReferencePlaceholder(conditions, ORG_FROM_RESTAURANT_SCOPE_PLACEHOLDER);
    if (bindsOrgScope && bindsRestaurantScope) return null;
    if (bindsOrgScope) return 'organization';
    if (bindsRestaurantScope) return 'restaurant';
    return null;
}

/**
 * Replaces a scope placeholder in a condition object with a concrete id array.
 * Walks one level deep — the conditions object is always flat in practice. A top-level
 * placeholder value becomes `{ $in: ids }`; a placeholder nested under an operator key
 * (e.g. `{ $in: PLACEHOLDER }`) becomes the raw `ids` array.
 */
export function substituteScopePlaceholder(
    conditions: Record<string, unknown>,
    placeholder: ScopePlaceholder,
    ids: string[]
): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(conditions)) {
        if (value === placeholder) {
            result[key] = { $in: ids };
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const inner = value as Record<string, unknown>;
            const substituted: Record<string, unknown> = {};
            for (const [innerKey, innerValue] of Object.entries(inner)) {
                substituted[innerKey] = innerValue === placeholder ? ids : innerValue;
            }
            result[key] = substituted;
        } else {
            result[key] = value;
        }
    }
    return result;
}
